import {
  AUTH_COOKIE_PUNCHOUT_HOOK_KEY,
  PUNCHOUT_BACK_TO_ERP_URL_COOKIE_PROP,
  PUNCHOUT_HOOK_URL_COOKIE_PROP,
} from '@/feature/auth/services/auth.constant'
import { useReactiveGetCookie } from 'cookies-next'

function safeParseJson(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value)

    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Record<string, unknown>
    }
  } catch {
    try {
      const parsed = JSON.parse(decodeURIComponent(value))

      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return null
    }
  }

  return null
}

/**
 * Parses the raw value of the punchout-hook-url cookie.
 * Handles both JSON formatted objects ({"hookUrl": "...", "backToERPUrl": "..."}) and direct URL strings.
 */
export function parsePunchoutHookCookie(cookieValue: string | undefined | null): string | null {
  if (!cookieValue) {
    return null
  }

  const parsed = safeParseJson(cookieValue)

  if (parsed) {
    const hookUrl = parsed[PUNCHOUT_HOOK_URL_COOKIE_PROP]

    if (typeof hookUrl === 'string' && hookUrl.trim()) {
      return hookUrl
    }

    const backToERPUrl = parsed[PUNCHOUT_BACK_TO_ERP_URL_COOKIE_PROP]

    if (typeof backToERPUrl === 'string' && backToERPUrl.trim()) {
      return backToERPUrl
    }
  }

  return cookieValue
}

/**
 * Parses the backToERPUrl value from the punchout-hook-url cookie if present.
 * Falls back to hookUrl if backToERPUrl is not set (e.g. OCI login flow).
 */
export function parsePunchoutBackToERPUrl(cookieValue: string | undefined | null): string | null {
  if (!cookieValue) {
    return null
  }

  const parsed = safeParseJson(cookieValue)

  if (parsed) {
    const backToERPUrl = parsed[PUNCHOUT_BACK_TO_ERP_URL_COOKIE_PROP]

    if (typeof backToERPUrl === 'string' && backToERPUrl.trim()) {
      return backToERPUrl
    }

    const hookUrl = parsed[PUNCHOUT_HOOK_URL_COOKIE_PROP]

    if (typeof hookUrl === 'string' && hookUrl.trim()) {
      return hookUrl
    }
  }

  if (cookieValue.startsWith('http')) {
    return cookieValue
  }

  return null
}

function useRawPunchoutHookCookie(): string | undefined {
  const cookie = useReactiveGetCookie()(AUTH_COOKIE_PUNCHOUT_HOOK_KEY)

  return typeof cookie === 'string' ? cookie : undefined
}

/**
 * Client hook to retrieve and parse the Punchout HOOK URL from cookies.
 */
export function usePunchoutHookUrl(): string | null {
  return parsePunchoutHookCookie(useRawPunchoutHookCookie())
}

/**
 * Client hook to retrieve and parse the backToERPUrl from punchout cookies.
 */
export function usePunchoutBackToERPUrl(): string | null {
  return parsePunchoutBackToERPUrl(useRawPunchoutHookCookie())
}
